import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography, Paper, Divider, SwitchCheckbox} from 'views/components';
import {UploadFileToS3, SecureFileGrid, Translate, translate} from 'views/module';
import {CloudUpload, DeleteForever} from 'views/icons';

const VulnerableSectorCheckEdit = (props) => {
  const {bioQuestions, fields, setFields, setIsComplete, getSignedFileUrl} = props;

  // Initialize
  React.useEffect(() => {
    const source = bioQuestions?.vulnerableSectorCheck || {};
    const resolved = {
      hasCheck: !!source.hasCheck,
      hasBeenVerified: !!source.hasBeenVerified,
      documents: source.documents || [],
    };
    setFields((prev) => ({
      ...prev,
      vulnerableSectorCheck: resolved,
    }));
  }, [bioQuestions, setFields]);

  // Memoized state
  const state = React.useMemo(() => {
    const current = fields?.vulnerableSectorCheck || {};
    return {
      hasCheck: !!current.hasCheck,
      hasBeenVerified: !!current.hasBeenVerified,
      documents: current.documents || [],
    };
  }, [fields?.vulnerableSectorCheck]);

  const {hasCheck, hasBeenVerified, documents} = state;

  // Completion status
  React.useEffect(() => {
    setIsComplete?.(Boolean(hasCheck) && documents.length > 0);
  }, [hasCheck, documents, setIsComplete]);

  // Update helper
  const updateState = (updater) => {
    setFields((prev) => {
      const current = prev?.vulnerableSectorCheck || {};
      const next = typeof updater === 'function' ? updater(current) : {...current, ...updater};
      return {
        ...prev,
        vulnerableSectorCheck: {
          hasCheck: !!next.hasCheck,
          hasBeenVerified: !!next.hasBeenVerified,
          documents: next.documents || [],
        },
      };
    });
  };

  // Handlers
  const handleToggle = (field) => (checked) => {
    updateState((prev) => ({...prev, [field]: !!checked}));
  };

  const handleRemoveDocument = (fileToRemove) => {
    updateState((prev) => ({
      ...prev,
      documents: prev.documents.filter((file) => file.src !== fileToRemove.src),
    }));
  };

  const getDocumentSignedUrl = async (file) => {
    const onComplete = (fileUrl) => {
      updateState((prev) => ({
        ...prev,
        documents: [...prev.documents, {name: file.name, type: file.type, src: fileUrl}],
      }));
    };
    return getSignedFileUrl('vulnerableSectorCheck', file, false, onComplete);
  };

  // Render
  return (
    <Stack spacing={3}>
      <Stack spacing={1.5}>
        <SwitchCheckbox
          value={hasCheck}
          onChange={handleToggle('hasCheck')}
          label={translate('caregiver.CaregiverBiography.VulnerableSectorCheck.HasCheck')}
        />
        <SwitchCheckbox
          value={hasBeenVerified}
          onChange={handleToggle('hasBeenVerified')}
          label={translate('caregiver.CaregiverBiography.VulnerableSectorCheck.HasBeenVerified')}
          disabled={!hasCheck}
        />
      </Stack>

      <Paper sx={{p: 3, borderRadius: 2}}>
        <Stack spacing={2}>
          <Typography variant='subtitle1' sx={{fontWeight: 600}}>
            <Translate expression='caregiver.CaregiverBiography.VulnerableSectorCheck.Documents' />
          </Typography>

          {documents.length ? (
            <>
              <SecureFileGrid
                files={documents}
                menuItems={[
                  {
                    Icon: DeleteForever,
                    text: translate('generic.Delete'),
                    handler: handleRemoveDocument,
                  },
                ]}
              />
              <Divider />
            </>
          ) : (
            <>
              <Typography variant='body2' color='text.secondary'>
                <Translate expression='caregiver.CaregiverBiography.VulnerableSectorCheck.EmptyState' />
              </Typography>
              <Divider />
            </>
          )}

          <UploadFileToS3 getSignedUrl={getDocumentSignedUrl}>
            <Box
              sx={(theme) => ({
                'width': '100%',
                'minHeight': 180,
                'borderRadius': '16px',
                'cursor': 'pointer',
                'display': 'flex',
                'alignItems': 'center',
                'justifyContent': 'center',
                'padding': theme.spacing(3),
                'border': `2px dashed ${
                  theme.palette.mode === 'light' ?
                    'rgba(0, 0, 0, 0.12)' :
                    'rgba(255, 255, 255, 0.25)'
                }`,
                'background':
                  theme.palette.mode === 'light' ?
                    'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 247, 250, 0.95))' :
                    theme.palette.background.paper,
                'boxShadow':
                  theme.palette.mode === 'light' ?
                    '0 12px 24px rgba(31, 41, 55, 0.08)' :
                    '0 16px 32px rgba(0, 0, 0, 0.45)',
                'transition': 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  boxShadow:
                    theme.palette.mode === 'light' ?
                      '0 18px 38px rgba(31, 41, 55, 0.12)' :
                      '0 20px 40px rgba(0, 0, 0, 0.55)',
                  transform: 'translateY(-2px)',
                  background:
                    theme.palette.mode === 'light' ?
                      'linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(245, 247, 250, 1))' :
                      theme.palette.background.default,
                },
                '&:focus-within': {
                  borderColor: theme.palette.primary.main,
                  boxShadow:
                    theme.palette.mode === 'light' ?
                      `0 0 0 4px ${theme.palette.primary.main}33` :
                      `0 0 0 4px ${theme.palette.primary.main}4d`,
                },
              })}
            >
              <Stack spacing={1.25} alignItems='center' textAlign='center'>
                <Box
                  sx={(theme) => ({
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor:
                      theme.palette.mode === 'light' ?
                        `${theme.palette.primary.main}14` :
                        `${theme.palette.primary.main}26`,
                    color: theme.palette.primary.main,
                  })}
                >
                  <CloudUpload fontSize='medium' />
                </Box>
                <Typography variant='subtitle1' color='text.primary' sx={{fontWeight: 600}}>
                  <Translate expression='caregiver.CaregiverBiography.VulnerableSectorCheck.UploadTitle' />
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  <Translate expression='caregiver.CaregiverBiography.VulnerableSectorCheck.UploadDescription' />
                </Typography>
              </Stack>
            </Box>
          </UploadFileToS3>
        </Stack>
      </Paper>
    </Stack>
  );
};

VulnerableSectorCheckEdit.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  fields: PropTypes.shape({
    vulnerableSectorCheck: PropTypes.shape({
      hasCheck: PropTypes.bool,
      hasBeenVerified: PropTypes.bool,
      documents: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            src: PropTypes.string.isRequired,
          }),
      ),
    }),
  }).isRequired,
  setFields: PropTypes.func.isRequired,
  setIsComplete: PropTypes.func,
  getSignedFileUrl: PropTypes.func.isRequired,
};

export default React.memo(VulnerableSectorCheckEdit);
